import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { Typography, Box } from '../../../1-primative';
import { Card, CardContent } from '../../../2-component/card/card';
import { ModelPortfolioData } from '../../../4-module/workflowCompletion/subSteps';
import { PieChartSecurities } from '../../../3-pattern/pieChartSecurities/pieChartSecurities';
import {
  SecuritiesSelectionTable, Security, SecurityTableColumns, SecurityTableOptions,
} from '../../../3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { getBackendLanguage } from '../../../../assets/i18n/config';
import { Button } from '../../../2-component/button/button';
import { UPDATE_MODEL_PORTFOLIO } from '../../../4-module/workflowCompletion/subSteps/queries';
import { useGlobalToast } from '../../../../providers/globalToastProvider';
import { IconButton } from '../../../2-component/iconButton/iconButton';
import { usePermissions } from '../../../../providers/userContextProvider';
import { ValidateSecuritiesDialogBox } from '../../../4-module/workflowCompletion/subSteps/addModelPortfolioSecurities/components/validateSecuritiesDialogBox';
import DownloadModelPortfolioSecurities from './downloadModelPortfolioSecurities';

type ColorType = 'supportOne' | 'supportTwo' | 'supportThree' | 'supportFour' | 'supportFive' | 'supportSix';

const COLOR_LIST: ColorType[] = ['supportOne', 'supportTwo', 'supportThree', 'supportFour', 'supportFive', 'supportSix'];

const getAssetClass = (security: Security): string => security.financialProduct.primaryAssetClass?.translatedName?.[getBackendLanguage()]
  || security.financialProduct.primaryAssetClass?.name
  || '';

const assignColorsToSecurities = (securities: Security[]): Security[] => {
  const seenAssetClasses = new Set<string>();
  const orderedAssetClasses = securities.reduce<string[]>((acc, sec) => {
    const assetClass = getAssetClass(sec);
    if (assetClass && !seenAssetClasses.has(assetClass)) {
      seenAssetClasses.add(assetClass);
      acc.push(assetClass);
    }
    return acc;
  }, []);

  const colorMap = orderedAssetClasses.reduce<Record<string, ColorType>>((acc, assetClass, i) => ({
    ...acc,
    [assetClass]: COLOR_LIST[i % COLOR_LIST.length],
  }), {});

  return securities.map((sec) => ({
    ...sec,
    color: getAssetClass(sec) ? colorMap[getAssetClass(sec)] : 'supportOne',
  }));
};

interface ModelPortfolioAssetAllocationProps {
  model: ModelPortfolioData;
  setModel: (v: ModelPortfolioData) => void;
  loading: boolean;
}

const ModelPortfolioAssetAllocation = ({
  model,
  setModel,
  loading,
}: ModelPortfolioAssetAllocationProps) => {
  const [securities, setSecurities] = useState<Security[]>([]);
  const [originalSecurities, setOriginalSecurities] = useState<Security[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation(['modelPortfolios', 'shared']);
  const { showToast } = useGlobalToast();
  const { permissions } = usePermissions();
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [totalPercentage, setTotalPercentage] = useState(0);

  const canWrite = (): boolean => (permissions.includes('write:model_portfolios') || false);
  const canRead = (): boolean => (permissions.includes('read:model_portfolios') || false);

  const [updateModelPortfolio] = useMutation(UPDATE_MODEL_PORTFOLIO, {
    refetchQueries: ['fetchModelPortfolio'],
  });

  useEffect(() => {
    if (model?.children) {
      const coloredSecurities = assignColorsToSecurities(model.children as Security[]);
      setSecurities(coloredSecurities);

      if (!originalSecurities.length) {
        setOriginalSecurities(JSON.parse(JSON.stringify(coloredSecurities)));
      }
    }
  }, [model, originalSecurities.length]);

  useEffect(() => {
    const hasChange = securities.length !== originalSecurities.length
      || securities.some((security, index) => {
        const original = originalSecurities[index];
        if (!original) return true;

        return security.percentage !== original.percentage
          || security.taxRank !== original.taxRank
          || security.financialProduct.id !== original.financialProduct.id;
      });

    // Check if any security is missing required fields
    const hasInvalidSecurities = securities.some((security) => (!security.financialProduct.isCash && !security.taxRank)
      || !security.financialProduct
      || !security.financialProduct.id
      || security.percentage === undefined
      || security.percentage === null
      || !security.financialProduct.primaryAssetClass);

    const total = securities.reduce((sum, security) => sum + (security.percentage || 0), 0);
    setTotalPercentage(Number(total.toFixed(2)));

    setHasChanges(hasChange && !hasInvalidSecurities);
  }, [securities, originalSecurities]);

  const updateSecurities = useCallback((newSecurities: Security[]) => {
    const coloredSecurities = assignColorsToSecurities(newSecurities);
    setSecurities(coloredSecurities);
    if (model) {
      setModel({
        ...model,
        children: coloredSecurities,
      });
    }
  }, [model, setModel]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = useCallback(() => {
    setSecurities(originalSecurities);
    if (model) {
      setModel({
        ...model,
        children: originalSecurities,
      });
    }
    setHasChanges(false);
    setIsEditing(false);
  }, [model, originalSecurities, setModel]);

  const handleSave = async () => {
    if (!model?.id || !hasChanges) return;

    // Calculate total percentage
    const total = (model.children as Security[])?.reduce((sum, allocation) => sum + (allocation.percentage || 0), 0) || 0;
    const roundedTotal = Number(total.toFixed(2));
    setTotalPercentage(roundedTotal);
    if (roundedTotal > 100) {
      setShowValidationDialog(true);
      return;
    }
    setIsSaving(true);
    try {
      const { errors } = await updateModelPortfolio({
        variables: {
          input: {
            modelPortfolioId: model.id,
            children: securities
              .filter((security) => !security.financialProduct.isCash)
              .map((security) => ({
                percentage: security.percentage,
                financialProductId: security.financialProduct.id,
                isCashEquivalent: security.isCashEquivalent,
              })),
          },
        },
      });

      if (errors) {
        showToast({
          message: errors[0].message,
          severity: 'error',
        });
        setSecurities(originalSecurities);
        if (model) {
          setModel({
            ...model,
            children: originalSecurities,
          });
        }
      } else {
        setOriginalSecurities(securities);
        showToast({
          message: t('modelPortfolios:portfolio.success'),
          severity: 'success',
        });
        setIsEditing(false);
      }
    } catch (error: any) {
      showToast({
        message: error.message,
        severity: 'error',
      });
      setSecurities(originalSecurities);
      if (model) {
        setModel({
          ...model,
          children: originalSecurities,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSecurity = useCallback((index: number, security: Security) => {
    const updatedSecurities = [...securities];
    updatedSecurities[index] = security;
    updateSecurities(updatedSecurities);
  }, [securities, updateSecurities]);

  const handleRemoveSecurity = useCallback((index: number) => {
    const updatedSecurities = securities.filter((_, i) => i !== index);
    updateSecurities(updatedSecurities);
  }, [securities, updateSecurities]);

  const handleAddSecurity = useCallback(() => {
    const nextTaxRank = securities.length > 0
      ? Math.max(...securities.map((security) => security.taxRank ?? 0)) + 1
      : 1;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newSecurity: Security = {
      financialProduct: {
        id: tempId,
        name: '',
        primaryAssetClass: undefined,
        translatedName: {
          en: '',
          fr: '',
        },
      },
      isCashEquivalent: false,
      taxRank: nextTaxRank,
      percentage: 0,
      id: tempId,
      color: 'supportOne',
    };

    updateSecurities([...securities, newSecurity]);
  }, [securities, updateSecurities]);

  const handleUpdateTaxRank = useCallback((index: number, value: number) => {
    const updatedSecurities = [...securities];
    updatedSecurities[index].taxRank = value;
    updateSecurities(updatedSecurities);
  }, [securities, updateSecurities]);

  const handleUpdateTargetPercentage = useCallback((index: number, value: number) => {
    const updatedSecurities = [...securities];
    updatedSecurities[index].percentage = value;
    updateSecurities(updatedSecurities);
  }, [securities, updateSecurities]);

  const handleReorderSecurities = useCallback((newSecurities: Security[]) => {
    const cashSecurities = newSecurities.filter((security) => security.financialProduct.isCash);
    const nonCashSecurities = newSecurities.filter((security) => !security.financialProduct.isCash);

    const updatedSecurities = [...nonCashSecurities, ...cashSecurities].map((security, index) => ({
      ...security,
      financialProduct: {
        ...security.financialProduct,
        id: security.financialProduct.id,
      },
      id: security.id || security.financialProduct.id,
      taxRank: security.financialProduct.isCash ? undefined : index + 1,
      percentage: security.percentage,
      name: security.financialProduct.name,
    }));

    updateSecurities(updatedSecurities);
  }, [updateSecurities]);

  const tableColumns: SecurityTableColumns = {
    security: {
      enabled: true,
      label: { en: 'Security', fr: 'Sécurité' },
    },
    assetClass: {
      enabled: true,
      label: { en: 'Asset Class', fr: 'Classe d\'actifs' },
    },
    taxRank: {
      enabled: true,
      label: { en: 'Tax Rank', fr: 'Rang fiscal' },
    },
    targetPercentage: {
      enabled: true,
      label: { en: 'Target %', fr: 'Cible %' },
    },
    buttonRemoveSecurity: {
      enabled: true,
    },
  };

  const options: SecurityTableOptions = {
    securityAllocationChart: {
      columns: [tableColumns],
    },
  };

  if (!canRead()) {
    return (
      <Box>
        <Typography
          data-testid="model-portfolio-asset-allocation-no-access-text"
          variant="displayLarge"
          sx={{ fontWeight: 600, fontSize: '20px' }}
        >
          {t('modelPortfolios:portfolio.noAccess')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" gap={2}>
        <Box width={300}>
          <Card>
            <CardContent>
              <PieChartSecurities
                testId="model-portfolio-asset-allocation-pie-chart"
                securities={securities}
                modelPortfolioName={model?.translatedName?.[getBackendLanguage()] ?? ''}
                portfolioTemplate={model?.portfolioTemplate}
              />
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  data-testid="model-portfolio-asset-allocation-title"
                  variant="headingSmall"
                >
                  {t('portfolio.tabs.assetAllocation')}
                </Typography>
                <Box display="flex" gap={1}>
                  {isEditing ? (
                    <>
                      <Button
                        dataTestId="model-portfolio-asset-allocation-cancel-button"
                        variant="tonal"
                        label={t('shared:cancel')}
                        onClick={handleCancel}
                        sx={{ mr: 1 }}
                        size="sm"
                      />
                      <Button
                        dataTestId="model-portfolio-asset-allocation-save-button"
                        variant="filled"
                        label={t('shared:save')}
                        onClick={handleSave}
                        disabled={!hasChanges || isSaving || totalPercentage > 100}
                        loading={isSaving}
                        size="sm"
                      />
                    </>
                  ) : (
                    <>
                      {canRead() && (
                        <DownloadModelPortfolioSecurities
                          modelPortfolioId={model?.id ?? ''}
                          onDownloadComplete={() => {}}
                        />
                      )}
                      {canWrite() && (
                        <IconButton
                          onClick={handleEdit}
                          sx={{ color: '#000000' }}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
            {isEditing && (
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2, ml: 3, mb: 1 }}>
                <InfoOutlinedIcon fontSize='small' data-testid={'info-taxrank-edit-sercurity-icon'} />
                <Typography
                  variant="bodyMedium"
                  data-testid={'info-taxrank-edit-sercurity-text'}
                >
                  {t('modelPortfolios:portfolio.securitiesOrderedByTaxRank')}
                </Typography>
              </Box>
            )}
            <SecuritiesSelectionTable
              editable={isEditing}
              securities={securities}
              options={options}
              onRemoveSecurity={handleRemoveSecurity}
              onUpdateSecurity={handleUpdateSecurity}
              onAddSecurity={handleAddSecurity}
              onUpdateTaxRank={handleUpdateTaxRank}
              onUpdateTargetPercentage={handleUpdateTargetPercentage}
              onReorderSecurities={handleReorderSecurities}
              ignoreBorderStyling
              sortable
            />
          </Card>
        </Box>
      </Box>
      <ValidateSecuritiesDialogBox
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        title={t('modelPortfolios:portfolio.exceedsTotalAllocation')}
        bodyText={t('modelPortfolios:portfolio.totalAllocationError', {
          totalPercentage: `<span style="color: #FF0000; font-weight: bold">${totalPercentage}%</span>`,
          maxPercentage: '<span style="color: #000000; font-weight: bold">100%</span>',
        })}
        confirmButtonText={t('modelPortfolios:portfolio.ok')}
        onConfirm={() => setShowValidationDialog(false)}
        disableCancelButton
      />
    </Box>
  );
};

export default ModelPortfolioAssetAllocation;
